import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiUrl } from '../../utils/apiBase';

interface TrechoTerrestre {
  id: string;
  origem: string;
  destino: string;
  data: string;
  tipo: 'ida' | 'volta';
  distancia_km?: number;
  pedagio_estimado?: number;
  combustivel_estimado?: number;
  consumo_medio?: number; // km/l
  preco_combustivel?: number; // R$/l
}

interface EstimativaTerrestre {
  distancia_km: number;
  pedagio_estimado: number;
  combustivel_estimado: number;
  custo_total: number;
  duracao_estimada: string;
  consumo_medio: number;
  preco_combustivel: number;
  rota_resumo?: string;
  fonte_distancia?: string;
  fonte_pedagio?: string;
  pedagio_nota?: string;
  nota?: string;
  google_maps_url?: string;
  litros_necessarios?: number;
}

// Autocomplete simples para cidades brasileiras
export const CidadeSearch: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}> = ({ value, onChange, placeholder = '🔍 Buscar cidade...' }) => {
  const [query, setQuery] = useState(value || '');
  const [resultados, setResultados] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (value !== query && !isOpen) {
      setQuery(value || '');
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const buscarCidades = useCallback(async (termo: string) => {
    if (termo.length < 2) {
      setResultados([]);
      setIsOpen(false);
      return;
    }
    setLoading(true);
    try {
      const resp = await fetch(apiUrl(`/rota-terrestre/cidades?q=${encodeURIComponent(termo)}`));
      if (resp.ok) {
        const data = await resp.json();
        setResultados(data);
        setIsOpen(data.length > 0);
      }
    } catch {
      // fallback: não mostra dropdown
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => buscarCidades(val), 300);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        value={query}
        onChange={handleInputChange}
        placeholder={placeholder}
        className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-400/50 bg-white"
      />
      {loading && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          <div className="w-3 h-3 border-2 border-green-400 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      {isOpen && resultados.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
          {resultados.map((cidade, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => {
                setQuery(cidade);
                onChange(cidade);
                setIsOpen(false);
              }}
              className="w-full px-3 py-1.5 text-xs text-left hover:bg-green-50 transition-colors border-b border-gray-100 last:border-0"
            >
              📍 {cidade}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Componente de estimativa de custos
const CustoEstimado: React.FC<{
  estimativa: EstimativaTerrestre | null;
  loading: boolean;
  erro: string;
}> = ({ estimativa, loading, erro }) => {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[10px] text-gray-400 py-1">
        <div className="w-3 h-3 border-2 border-green-400 border-t-transparent rounded-full animate-spin"></div>
        Calculando rota...
      </div>
    );
  }
  if (erro) {
    return <div className="text-[10px] text-red-400 py-1">⚠️ {erro}</div>;
  }
  if (!estimativa) return null;

  return (
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-2 border border-green-200 space-y-1 mt-1">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold text-green-700 uppercase tracking-wider">📊 Custos da Rota</span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400">{estimativa.duracao_estimada}</span>
          {estimativa.google_maps_url && (
            <a href={estimativa.google_maps_url} target="_blank" rel="noopener noreferrer" className="text-[9px] text-blue-500 hover:text-blue-700 underline">
              📍 Google Maps
            </a>
          )}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="text-center p-1.5 bg-white/70 rounded-lg">
          <div className="text-[9px] text-gray-500 flex items-center justify-center gap-0.5">
            🛣️ Distância
            {estimativa.fonte_distancia === 'osrm' && <span className="text-green-500" title="Distância rodoviária real (OpenStreetMap)">✓</span>}
          </div>
          <div className="text-xs font-bold text-gray-700">{estimativa.distancia_km.toLocaleString('pt-BR')} km</div>
          <div className="text-[8px] text-gray-400">{estimativa.fonte_distancia === 'osrm' ? 'Rota real' : '~Estimativa'}</div>
        </div>
        <div className="text-center p-1.5 bg-white/70 rounded-lg">
          <div className="text-[9px] text-gray-500 flex items-center justify-center gap-0.5">
            🚧 Pedágio
            {estimativa.fonte_pedagio === 'tabela_antt' && <span className="text-green-500" title="Valor baseado em dados de concessionárias">✓</span>}
          </div>
          <div className="text-xs font-bold text-orange-600">R$ {estimativa.pedagio_estimado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
          <div className="text-[8px] text-gray-400">{estimativa.fonte_pedagio === 'tabela_antt' ? 'Dados ANTT' : '~Estimativa'}</div>
        </div>
        <div className="text-center p-1.5 bg-white/70 rounded-lg">
          <div className="text-[9px] text-gray-500">⛽ Combustível</div>
          <div className="text-xs font-bold text-blue-600">R$ {estimativa.combustivel_estimado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
          <div className="text-[8px] text-gray-400">{estimativa.litros_necessarios ? `${estimativa.litros_necessarios}L` : ''}</div>
        </div>
      </div>
      <div className="flex items-center justify-end pt-1 border-t border-green-200">
        <span className="text-xs font-bold text-green-700">
          Total: R$ {estimativa.custo_total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </span>
      </div>
    </div>
  );
};

// Componente principal de Rota Terrestre
export const RotaTerrestreField: React.FC<{
  rotasAtuais: any[];
  campoId: string;
  updateFieldValue: (campoId: string, valor: any) => void;
  consumoMedioPadrao?: number;
  precoCombustivelPadrao?: number;
}> = ({ rotasAtuais, campoId, updateFieldValue, consumoMedioPadrao = 10, precoCombustivelPadrao = 6.0 }) => {
  const [estimativas, setEstimativas] = useState<Record<string, EstimativaTerrestre>>({});
  const [loadings, setLoadings] = useState<Record<string, boolean>>({});
  const [erros, setErros] = useState<Record<string, string>>({});
  const fetchedRef = useRef<Record<string, boolean>>({});
  const rotasRef = useRef(rotasAtuais);
  const updateRef = useRef(updateFieldValue);
  const campoIdRef = useRef(campoId);
  
  useEffect(() => { rotasRef.current = rotasAtuais; }, [rotasAtuais]);
  useEffect(() => { updateRef.current = updateFieldValue; }, [updateFieldValue]);
  useEffect(() => { campoIdRef.current = campoId; }, [campoId]);

  const getChave = (trecho: any) => {
    return trecho.origem && trecho.destino ? `${trecho.origem}_${trecho.destino}` : '';
  };

  const calcularRota = useCallback(async (origem: string, destino: string, consumoMedio: number, precoCombustivel: number, chave: string, trechoIdx: number) => {
    if (fetchedRef.current[chave]) return;
    fetchedRef.current[chave] = true;
    setLoadings(prev => ({ ...prev, [chave]: true }));
    setErros(prev => ({ ...prev, [chave]: '' }));
    try {
      const resp = await fetch(apiUrl('/rota-terrestre/calcular'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origem, destino, consumo_medio: consumoMedio, preco_combustivel: precoCombustivel })
      });
      const result = await resp.json();
      if (resp.ok && result.distancia_km !== undefined) {
        setEstimativas(prev => ({ ...prev, [chave]: result }));
        // Salvar custos no trecho para o view mode ter acesso
        const novas = [...rotasRef.current];
        if (novas[trechoIdx]) {
          novas[trechoIdx] = {
            ...novas[trechoIdx],
            _distancia_km: result.distancia_km,
            _pedagio_estimado: result.pedagio_estimado,
            _combustivel_estimado: result.combustivel_estimado,
            _custo_total: result.custo_total,
            _duracao_estimada: result.duracao_estimada,
            _google_maps_url: result.google_maps_url,
          };
          updateRef.current(campoIdRef.current, novas);
        }
      } else {
        setErros(prev => ({ ...prev, [chave]: result.error || 'Erro ao calcular rota' }));
      }
    } catch {
      setErros(prev => ({ ...prev, [chave]: 'Erro de conexão' }));
    } finally {
      setLoadings(prev => ({ ...prev, [chave]: false }));
    }
  }, []);

  useEffect(() => {
    rotasAtuais.forEach((trecho, idx) => {
      if (trecho.origem && trecho.destino) {
        const chave = getChave(trecho);
        if (chave && !fetchedRef.current[chave]) {
          calcularRota(
            trecho.origem,
            trecho.destino,
            consumoMedioPadrao,
            precoCombustivelPadrao,
            chave,
            idx
          );
        }
      }
    });
  }, [rotasAtuais, calcularRota]);

  const handleRecalcular = (trecho: any, idx: number) => {
    const chave = getChave(trecho);
    if (chave) {
      delete fetchedRef.current[chave];
      setEstimativas(prev => { const n = { ...prev }; delete n[chave]; return n; });
      calcularRota(
        trecho.origem,
        trecho.destino,
        consumoMedioPadrao,
        precoCombustivelPadrao,
        chave,
        idx
      );
    }
  };

  // Calcular totais
  const totais = rotasAtuais.reduce((acc, trecho) => {
    const chave = getChave(trecho);
    const est = chave ? estimativas[chave] : null;
    if (est) {
      acc.km += est.distancia_km;
      acc.pedagio += est.pedagio_estimado;
      acc.combustivel += est.combustivel_estimado;
      acc.total += est.custo_total;
    }
    return acc;
  }, { km: 0, pedagio: 0, combustivel: 0, total: 0 });

  return (
    <div className="space-y-3">
      {rotasAtuais.length > 0 ? (
        <div className="space-y-2">
          {rotasAtuais.map((trecho: any, idx: number) => {
            const chave = getChave(trecho);
            return (
              <div key={trecho.id || idx} className={`relative rounded-xl border-2 overflow-hidden ${trecho.tipo === 'ida' ? 'border-green-200 bg-gradient-to-r from-green-50 to-white' : 'border-orange-200 bg-gradient-to-r from-orange-50 to-white'}`}>
                <div className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider flex items-center justify-between ${trecho.tipo === 'ida' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
                  <span>{trecho.tipo === 'ida' ? '🚗 Trecho de Ida' : '🔙 Trecho de Volta'} #{idx + 1}</span>
                  <button onClick={() => { const novas = rotasAtuais.filter((_: any, i: number) => i !== idx); updateFieldValue(campoId, novas); }} className="text-red-400 hover:text-red-600 p-0.5 rounded hover:bg-white/50" title="Remover trecho">✕</button>
                </div>
                <div className="p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 mb-0.5">📍 Origem</label>
                      <CidadeSearch
                        value={trecho.origem || ''}
                        onChange={(value) => {
                          const novas = [...rotasAtuais];
                          novas[idx] = { ...novas[idx], origem: value };
                          updateFieldValue(campoId, novas);
                        }}
                        placeholder="Cidade de origem..."
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 mb-0.5">📍 Destino</label>
                      <CidadeSearch
                        value={trecho.destino || ''}
                        onChange={(value) => {
                          const novas = [...rotasAtuais];
                          novas[idx] = { ...novas[idx], destino: value };
                          updateFieldValue(campoId, novas);
                        }}
                        placeholder="Cidade de destino..."
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 mb-0.5">📅 Data</label>
                      <input
                        type="date"
                        value={trecho.data || ''}
                        onChange={(e) => { const novas = [...rotasAtuais]; novas[idx] = { ...novas[idx], data: e.target.value }; updateFieldValue(campoId, novas); }}
                        className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-400/50 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-500 mb-0.5">🔄 Tipo</label>
                      <select
                        value={trecho.tipo || 'ida'}
                        onChange={(e) => { const novas = [...rotasAtuais]; novas[idx] = { ...novas[idx], tipo: e.target.value }; updateFieldValue(campoId, novas); }}
                        className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-400/50 bg-white"
                      >
                        <option value="ida">🚗 Ida</option>
                        <option value="volta">🔙 Volta</option>
                      </select>
                    </div>
                  </div>

                </div>
                {/* Visualização da rota */}
                {trecho.origem && trecho.destino && (
                  <div className={`px-3 pb-2 flex items-center gap-2 text-[10px] ${trecho.tipo === 'ida' ? 'text-green-600' : 'text-orange-600'}`}>
                    <span className="font-bold">{trecho.origem}</span>
                    <span className="flex-1 border-t-2 border-dashed border-current relative">
                      <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-white px-1">🚗</span>
                    </span>
                    <span className="font-bold">{trecho.destino}</span>
                    {trecho.data && <span className="ml-2 text-gray-400">({new Date(trecho.data + 'T00:00:00').toLocaleDateString('pt-BR')})</span>}
                  </div>
                )}
                {/* Estimativas de custo */}
                {chave && (
                  <div className="px-3 pb-2">
                    <CustoEstimado
                      estimativa={estimativas[chave] || null}
                      loading={loadings[chave] || false}
                      erro={erros[chave] || ''}
                    />
                    {estimativas[chave] && !loadings[chave] && (
                      <button type="button" onClick={() => handleRecalcular(trecho, idx)} className="mt-1 text-[9px] text-gray-400 hover:text-green-500 underline transition-colors">
                        🔄 Recalcular rota
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-4 text-gray-400 text-xs border-2 border-dashed border-gray-200 rounded-xl">
          <span className="text-2xl block mb-1">🚗</span>
          Nenhum trecho terrestre adicionado ainda
        </div>
      )}

      {/* Resumo total */}
      {rotasAtuais.length > 0 && rotasAtuais.some((t: any) => t.origem && t.destino) && totais.km > 0 && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-3 border border-green-200">
          <div className="text-[10px] font-bold text-green-700 uppercase tracking-wider mb-2">📋 Resumo da Viagem Terrestre</div>
          <div className="space-y-1 mb-2">
            {rotasAtuais.filter((t: any) => t.origem && t.destino).map((t: any, i: number) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${t.tipo === 'ida' ? 'bg-green-500' : 'bg-orange-500'}`}>{i + 1}</span>
                <span className="text-gray-700">{t.origem}</span>
                <span className="text-gray-400">{t.tipo === 'ida' ? '→' : '←'}</span>
                <span className="text-gray-700">{t.destino}</span>
                {t.data && <span className="text-gray-400 ml-auto text-[10px]">{new Date(t.data + 'T00:00:00').toLocaleDateString('pt-BR')}</span>}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-4 gap-2 pt-2 border-t border-green-200">
            <div className="text-center">
              <div className="text-[9px] text-gray-500">Total KM</div>
              <div className="text-xs font-bold text-gray-700">{totais.km.toLocaleString('pt-BR')} km</div>
            </div>
            <div className="text-center">
              <div className="text-[9px] text-gray-500">Pedágio</div>
              <div className="text-xs font-bold text-orange-600">R$ {totais.pedagio.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
            </div>
            <div className="text-center">
              <div className="text-[9px] text-gray-500">Combustível</div>
              <div className="text-xs font-bold text-blue-600">R$ {totais.combustivel.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
            </div>
            <div className="text-center">
              <div className="text-[9px] text-gray-500 font-bold">TOTAL</div>
              <div className="text-sm font-bold text-green-700">R$ {totais.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
            </div>
          </div>
        </div>
      )}

      {/* Resumo simples sem estimativas */}
      {rotasAtuais.length > 0 && rotasAtuais.some((t: any) => t.origem && t.destino) && totais.km === 0 && (
        <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl p-3 border border-slate-200">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">📋 Resumo da Viagem</div>
          <div className="space-y-1">
            {rotasAtuais.filter((t: any) => t.origem && t.destino).map((t: any, i: number) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${t.tipo === 'ida' ? 'bg-green-500' : 'bg-orange-500'}`}>{i + 1}</span>
                <span className="text-gray-700">{t.origem}</span>
                <span className="text-gray-400">{t.tipo === 'ida' ? '→' : '←'}</span>
                <span className="text-gray-700">{t.destino}</span>
                {t.data && <span className="text-gray-400 ml-auto text-[10px]">{new Date(t.data + 'T00:00:00').toLocaleDateString('pt-BR')}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Botões de adicionar */}
      <div className="flex gap-2">
        <button
          onClick={() => {
            const novoTrecho: TrechoTerrestre = { id: `rota_t_${Date.now()}`, origem: '', destino: '', data: '', tipo: 'ida' };
            updateFieldValue(campoId, [...rotasAtuais, novoTrecho]);
          }}
          type="button"
          className="flex-1 px-3 py-2 border-2 border-dashed border-green-300 text-green-600 hover:bg-green-50 rounded-xl text-xs font-medium transition-colors"
        >
          🚗 + Trecho de Ida
        </button>
        <button
          onClick={() => {
            const novoTrecho: TrechoTerrestre = { id: `rota_t_${Date.now()}`, origem: '', destino: '', data: '', tipo: 'volta' };
            updateFieldValue(campoId, [...rotasAtuais, novoTrecho]);
          }}
          type="button"
          className="flex-1 px-3 py-2 border-2 border-dashed border-orange-300 text-orange-600 hover:bg-orange-50 rounded-xl text-xs font-medium transition-colors"
        >
          🔙 + Trecho de Volta
        </button>
      </div>
    </div>
  );
};

// Componente de resumo para view mode - auto-calcula custos se não estiverem salvos
export const RotaTerrestreResumo: React.FC<{
  trechos: any[];
  consumoMedioPadrao?: number;
  precoCombustivelPadrao?: number;
}> = ({ trechos, consumoMedioPadrao = 10, precoCombustivelPadrao = 6.0 }) => {
  const [custos, setCustos] = useState<Record<string, EstimativaTerrestre>>({});
  const [carregando, setCarregando] = useState(false);
  const fetchedRef = useRef<Record<string, boolean>>({});

  const trechosValidos = trechos.filter((t: any) => t.origem && t.destino);

  useEffect(() => {
    if (trechosValidos.length === 0) return;

    // Se já tem custos salvos (_distancia_km), usar eles
    const todosTem = trechosValidos.every((t: any) => t._distancia_km);
    if (todosTem) {
      const custosFromSaved: Record<string, EstimativaTerrestre> = {};
      trechosValidos.forEach((t: any) => {
        const chave = `${t.origem}_${t.destino}`;
        custosFromSaved[chave] = {
          distancia_km: t._distancia_km,
          pedagio_estimado: t._pedagio_estimado || 0,
          combustivel_estimado: t._combustivel_estimado || 0,
          custo_total: t._custo_total || 0,
          duracao_estimada: t._duracao_estimada || '',
          consumo_medio: consumoMedioPadrao,
          preco_combustivel: precoCombustivelPadrao,
          google_maps_url: t._google_maps_url,
        };
      });
      setCustos(custosFromSaved);
      return;
    }

    // Se não tem, buscar da API
    const buscarCustos = async () => {
      setCarregando(true);
      const novosCustos: Record<string, EstimativaTerrestre> = {};
      
      for (const t of trechosValidos) {
        const chave = `${t.origem}_${t.destino}`;
        if (fetchedRef.current[chave]) continue;
        fetchedRef.current[chave] = true;
        
        // Se tem dados salvos, usar
        if (t._distancia_km) {
          novosCustos[chave] = {
            distancia_km: t._distancia_km,
            pedagio_estimado: t._pedagio_estimado || 0,
            combustivel_estimado: t._combustivel_estimado || 0,
            custo_total: t._custo_total || 0,
            duracao_estimada: t._duracao_estimada || '',
            consumo_medio: consumoMedioPadrao,
            preco_combustivel: precoCombustivelPadrao,
            google_maps_url: t._google_maps_url,
          };
          continue;
        }
        
        try {
          const resp = await fetch(apiUrl('/rota-terrestre/calcular'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ origem: t.origem, destino: t.destino, consumo_medio: consumoMedioPadrao, preco_combustivel: precoCombustivelPadrao })
          });
          const result = await resp.json();
          if (resp.ok && result.distancia_km !== undefined) {
            novosCustos[chave] = result;
          }
        } catch { /* ignorar erros no view mode */ }
      }
      
      setCustos(prev => ({ ...prev, ...novosCustos }));
      setCarregando(false);
    };
    
    buscarCustos();
  }, [trechosValidos.length]);

  if (trechosValidos.length === 0) {
    return <span className="text-gray-400 text-xs">Nenhuma rota terrestre definida</span>;
  }

  const totais = trechosValidos.reduce((acc, t) => {
    const chave = `${t.origem}_${t.destino}`;
    const est = custos[chave];
    if (est) {
      acc.km += est.distancia_km || 0;
      acc.pedagio += est.pedagio_estimado || 0;
      acc.combustivel += est.combustivel_estimado || 0;
      acc.total += est.custo_total || 0;
    }
    return acc;
  }, { km: 0, pedagio: 0, combustivel: 0, total: 0 });

  return (
    <div className="space-y-2">
      {/* Lista de trechos */}
      {trechosValidos.map((t: any, i: number) => {
        const chave = `${t.origem}_${t.destino}`;
        const est = custos[chave];
        return (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${t.tipo === 'ida' ? 'bg-green-500' : 'bg-orange-500'}`}>{i + 1}</span>
            <span className="font-medium">{t.origem}</span>
            <span className="text-gray-400">{t.tipo === 'ida' ? '→' : '←'}</span>
            <span className="font-medium">{t.destino}</span>
            {est && <span className="text-gray-400 text-[10px]">({est.distancia_km} km)</span>}
            {t.data && <span className="text-gray-400 ml-auto text-[10px]">{new Date(t.data + 'T00:00:00').toLocaleDateString('pt-BR')}</span>}
          </div>
        );
      })}

      {/* Resumo de custos */}
      {carregando && (
        <div className="text-center py-2 text-xs text-gray-400">
          <span className="animate-pulse">⏳ Calculando custos...</span>
        </div>
      )}

      {!carregando && totais.km > 0 && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-2 border border-green-200 mt-1">
          <div className="text-[10px] font-bold text-green-700 uppercase tracking-wider mb-1">📊 Custos da Viagem</div>
          <div className="grid grid-cols-4 gap-2">
            <div className="text-center">
              <div className="text-[9px] text-gray-500">🛣️ Distância</div>
              <div className="text-xs font-bold text-gray-700">{totais.km.toLocaleString('pt-BR')} km</div>
            </div>
            <div className="text-center">
              <div className="text-[9px] text-gray-500">🚧 Pedágio</div>
              <div className="text-xs font-bold text-orange-600">R$ {totais.pedagio.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
            </div>
            <div className="text-center">
              <div className="text-[9px] text-gray-500">⛽ Combustível</div>
              <div className="text-xs font-bold text-blue-600">R$ {totais.combustivel.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
            </div>
            <div className="text-center">
              <div className="text-[9px] text-gray-500 font-bold">TOTAL</div>
              <div className="text-sm font-bold text-green-700">R$ {totais.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RotaTerrestreField;
